import React, { Fragment, useState, useMemo, useRef, useEffect } from "react";
import { Input, Row, Col } from "reactstrap";
import DataTable from "react-data-table-component";
import { Btn } from "../../../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import CommonModal from "../../../UiKits/Modals/common/modal";
import { toast } from "react-toastify";
import axios from "axios";
import config from "../../../../config";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

/* ALL AVAILABLE COLUMNS */

const ALL_COLUMNS = [
    { key: "property_name", label: "Property" },
    { key: "tenant_name", label: "Tenant" },
    { key: "tenant_code", label: "Tenant Code" },

    { key: "gross_sys", label: "Gross (Sys)", isMoney: true },
    { key: "gross_up", label: "Gross (Up)", isMoney: true },
    { key: "gross_diff", label: "Gross Diff", isMoney: true },

    { key: "net_sys", label: "Net (Sys)", isMoney: true },
    { key: "net_up", label: "Net (Up)", isMoney: true },
    { key: "net_diff", label: "Net Diff", isMoney: true },

    { key: "tax_sys", label: "Tax (Sys)", isMoney: true },
    { key: "tax_up", label: "Tax (Up)", isMoney: true },
    { key: "tax_diff", label: "Tax Diff", isMoney: true },

    { key: "disc_sys", label: "Disc (Sys)", isMoney: true },
    { key: "disc_up", label: "Disc (Up)", isMoney: true },
    { key: "disc_diff", label: "Disc Diff", isMoney: true },

    { key: "ret_sys", label: "Ret (Sys)", isMoney: true },
    { key: "ret_up", label: "Ret (Up)", isMoney: true },
    { key: "ret_diff", label: "Ret Diff", isMoney: true },

    { key: "live_dates", label: "Live Dates" },

    
    { key: "comment", label: "Comment" },
    { key: "discrepancy_remarks", label: "Discrepancy Comments" },
    
    { key: "status", label: "Status" },
    { key: "action", label: "Action" }
];

/* DEFAULT COLUMNS */

const DEFAULT_COLUMNS = [
    "property_name",
    "tenant_name",
    "tenant_code",
    // "gross_sys",
    // "gross_up",
    "gross_diff",
    // "net_sys",
    // "net_up",
    "net_diff",
    "tax_diff",
    "live_dates",
    "comment",
    "status",
    "action"
];

const TenantAuditSummary = ({
    tenantAudits,
    loading,
    propertySelected,
    isLocked,
    auditPeriodId,
    monthLabel,
    selectedYear,
    property_id,
    propertyName
}) => {

    const navigate = useNavigate();
    const token = localStorage.getItem("sessiontoken");
    const configheader = { headers: { Authorization: `${token}` } };
    const concessionaire_id = localStorage.getItem("concessionaireId");
    const login_name = localStorage.getItem("loginName");
    const login_id = localStorage.getItem("loginId");

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedColumns, setSelectedColumns] = useState(DEFAULT_COLUMNS);
    const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);
    const [commentModal, setCommentModal] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [commentText, setCommentText] = useState("");
    const columnDropdownRef = useRef(null);
    const TOLERANCE = Number(localStorage.getItem("auditTolerance") || 0);
    const [columnsLoaded, setColumnsLoaded] = useState(false);
    const [tooltip, setTooltip] = useState("");

    const handleMouseEnter = (text) => setTooltip(text);
    const handleMouseLeave = () => setTooltip("");

    useEffect(() => {

        const handleClickOutside = (event) => {

            if (
                columnDropdownRef.current &&
                !columnDropdownRef.current.contains(event.target)
            ) {
                setColumnDropdownOpen(false);
            }

        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);

    }, []);

    /* STATUS BADGE */

    const StatusBadge = ({ status }) => {

        const colors = {
            MATCHED: "success",
            NOT_MATCHED: "secondary",
            RECONCILED: "warning",
            PENDING: "dark"
        };

        return (
            <span className={`badge badge-light-${colors[status] || "secondary"}`}>
                {status.replace("_", " ")}
            </span>
        );
    };

    const insertToLog = (task) => {
        const data = {
            created_by: login_id,
            concessionaire_id: concessionaire_id,
            module: "Audit",
            task: `${login_name} ${task}`
        }
        axios
            .post(`${config.hostname}/log`, data, configheader)
            .then(() => {
                console.log("inserted to log table")
            })
            .catch((error) => {
                toast.error('Error')
                console.log(error);

            });
    }
    const formatMoney = (v) =>
        v != null
            ? Number(v).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            })
            : "—";

    const diff = (a, b) => {
        if (a == null || b == null) return null;
        return Number((b - a).toFixed(2));
    };

    /* TRANSFORM API DATA */

    const tableData = useMemo(() => {

        return tenantAudits.map((t) => {

            const sys = t.audit_values?.SYSTEM || {};
            const up = t.audit_values?.UPLOADED || {};

            return {
                ...t,

                property_name: t.property_name,
                tenant_name: t.tenant_name,
                tenant_code: t.tenant_code,

                gross_sys: sys.gross_sales,
                gross_up: up.gross_sales,
                gross_diff: diff(sys.gross_sales, up.gross_sales),

                net_sys: sys.net_sales,
                net_up: up.net_sales,
                net_diff: diff(sys.net_sales, up.net_sales),

                tax_sys: sys.tax,
                tax_up: up.tax,
                tax_diff: diff(sys.tax, up.tax),

                disc_sys: sys.discount,
                disc_up: up.discount,
                disc_diff: diff(sys.discount, up.discount),

                ret_sys: sys.returns,
                ret_up: up.returns,
                ret_diff: diff(sys.returns, up.returns),
                
                live_dates: t.live_dates ?? 0,
                
                comment: t.comment,
                discrepancy_remarks: t.discrepancy_remarks,

                status: t.status
            };

        });

    }, [tenantAudits]);

    /* FILTERING */

    const filteredData = useMemo(() => {

        let data = tableData;

        if (statusFilter) {
            data = data.filter((t) => t.status === statusFilter);
        }

        if (!search) return data;

        const q = search.toLowerCase();

        return data.filter(
            (t) =>
                t.tenant_name?.toLowerCase().includes(q) ||
                t.tenant_code?.toLowerCase().includes(q) ||
                t.property_name?.toLowerCase().includes(q)
        );

    }, [search, tableData, statusFilter]);

    /* COLUMN SELECTOR */

    const selectedCols = useMemo(() => {
        return ALL_COLUMNS.filter((col) => selectedColumns.includes(col.key));
    }, [selectedColumns]);

    /* ACTIONS */

    const handleView = (row) => {
        insertToLog(
            `Viewed audit details for ${row.tenant_name} (${row.property_name} - ${monthLabel} ${selectedYear})`
        );

        navigate(`${process.env.PUBLIC_URL}/admin/audit/tenant/${row._id}`, {
            // Rows carry their own period/lock when "All" properties is selected
            state: {
                tenant: row,
                auditPeriodId: row.audit_period_id ?? auditPeriodId,
                isLocked: row.is_locked ?? isLocked,
                monthLabel,
                selectedYear,
                property_id,
                propertyName
            }
        });

    };

    const handleReconcile = (row) => {

        insertToLog(
            `Opened reconciliation for ${row.tenant_name} (${row.property_name} - ${monthLabel} ${selectedYear})`
        );
        navigate(`${process.env.PUBLIC_URL}/admin/audit/reconciliation/${row._id}`, {
            state: { tenant: row, auditPeriodId: row.audit_period_id ?? auditPeriodId, monthLabel, selectedYear, from: "main", property_id, propertyName }
        });

    };

    const handleComment = (row) => {
        setSelectedTenant(row);
        setCommentText(row.comment || "");
        setCommentModal(true);
    };

    const saveComment = () => {

        if (!selectedTenant) return;

        axios
            .post(
                `${config.hostname}/audit/updateauditcomment`,
                {
                    tenant_audit_id: selectedTenant._id,
                    comment: commentText,
                    login_id: login_id
                },
                configheader
            )
            .then((res) => {

                toast.success(res.data.message || "Comment saved successfully");
                insertToLog(
                    `Updated audit comment for ${selectedTenant.tenant_name} (${selectedTenant.property_name} - ${monthLabel} ${selectedYear})`
                );
                selectedTenant.comment = commentText;
                setSelectedTenant({ ...selectedTenant });
                setCommentModal(false);

            })
            .catch((err) => {

                console.error(err);

                toast.error(
                    err?.response?.data?.message || "Failed to save comment"
                );

            });
    };

    /* DATATABLE COLUMNS */

    const columns = selectedCols.map((col) => {

        if (col.key === "action") {

            return {
                name: col.label,
                width: "260px",
                center: true,

                cell: (row) => (

                    <div style={{ display: "flex", gap: "6px", flexWrap: "nowrap" }}>

                        <Btn
                            attrBtn={{
                                size: "sm",
                                color: "primary",
                                style: { minWidth: "70px", flexShrink: 0 },
                                onClick: () => handleView(row)
                            }}
                        >
                            View
                        </Btn>

                        <Btn
                            attrBtn={{
                                size: "sm",
                                color: "primary",
                                style: { minWidth: "80px", flexShrink: 0 },
                                onClick: () => handleComment(row)
                            }}
                        >
                            Comment
                        </Btn>

                        {row.status === "NOT_MATCHED" && !(row.is_locked ?? isLocked) && (

                            <Btn
                                attrBtn={{
                                    size: "sm",
                                    color: "primary",
                                    style: { minWidth: "90px", flexShrink: 0 },
                                    onClick: () => handleReconcile(row)
                                }}
                            >
                                Reconcile
                            </Btn>

                        )}
                    </div>
                )
            };
        }

        if (col.key === "status") {

            return {
                name: col.label,
                width: "140px",
                center: true,
                cell: (row) => <StatusBadge status={row.status} />
            };

        }

        if (col.key === "live_dates") {
            return {
                name: col.label,
                selector: (row) => row[col.key],
                sortable: true,
                width: "120px",
                center: true,
                cell: (row) => row.live_dates ?? 0
            };
        }

        return {
            name: col.label,
            selector: (row) => row[col.key],
            sortable: true,
            minWidth: col.isMoney ? "140px" : "180px",

            cell: (row) => {

                const value = row[col.key];

                if (col.isMoney) {

                    if (col.key.includes("diff") && value !== null) {

                        const diffClass = Math.abs(value) <= TOLERANCE ? "diff-zero" : "diff-pos";

                        return (
                            <div className={`money ${diffClass}`}>
                                {formatMoney(value)}
                            </div>
                        );

                    }

                    return (
                        <div className="money">
                            {formatMoney(value)}
                        </div>
                    );

                }

                if (
                    col.key === "property_name" ||
                    col.key === "tenant_name" ||
                    col.key === "tenant_code"
                ) { return (<div style={{ whiteSpace: "nowrap" }}> {value} </div>); }
                return value ?? "—";
            }

        };

    });

    const handleExport = (format) => {
        if (!filteredData.length) {
            toast.error("No data to export");
            return;
        }

        if (format === "excel") {
            exportToExcel();
            insertToLog(
                `Exported audit summary to Excel for (${monthLabel} ${selectedYear})`
            );
        }
        if (format === "pdf") {
            exportToPDF();
            insertToLog(
                `Exported audit summary to PDF for (${monthLabel} ${selectedYear})`
            );
        }
    };


    const exportToExcel = () => {

        const exportColumns = selectedCols.filter(c => c.key !== "action");

        const exportRows = filteredData.map(row => {
            const obj = {};

            exportColumns.forEach(col => {
                obj[col.label] = row[col.key] ?? "";
            });

            return obj;
        });

        const worksheet = XLSX.utils.aoa_to_sheet([[]]);

        /* TITLE */

        XLSX.utils.sheet_add_aoa(
            worksheet,
            [["Tenant Audit Summary Report"]],
            { origin: "A1" }
        );

        worksheet["!merges"] = [{
            s: { r: 0, c: 0 },
            e: { r: 0, c: exportColumns.length - 1 }
        }];

        /* REPORT DETAILS */

        const metadata = [
            ["Month", `${monthLabel} ${selectedYear}`],
            ["Property", filteredData[0]?.property_name || "All"],
            ["Status Filter", statusFilter || "All"],
            ["Exported By", localStorage.getItem("loginName")],
            ["Exported On", new Date().toLocaleString()]
        ];

        XLSX.utils.sheet_add_aoa(worksheet, metadata, { origin: "A3" });

        /* HEADER */

        const startRow = metadata.length + 5;

        XLSX.utils.sheet_add_aoa(
            worksheet,
            [exportColumns.map(c => c.label)],
            { origin: `A${startRow}` }
        );

        /* DATA */

        XLSX.utils.sheet_add_json(worksheet, exportRows, {
            origin: `A${startRow + 1}`,
            skipHeader: true
        });

        /* COLUMN WIDTH */

        worksheet["!cols"] = exportColumns.map(col => ({
            wch: col.isMoney ? 16 : 24
        }));

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, "Audit Summary");

        XLSX.writeFile(
            workbook,
            `Tenant_Audit_Summary_${monthLabel}_${selectedYear}.xlsx`
        );

    };

    const exportToPDF = () => {

        const exportColumns = selectedCols.filter(c => c.key !== "action");

        const doc = new jsPDF("l", "pt", "a4");

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        /* TITLE */

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");

        doc.text(
            "Tenant Audit Summary Report",
            pageWidth / 2,
            30,
            { align: "center" }
        );

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");

        let startY = 60;

        const metadata = [
            `Month: ${monthLabel} ${selectedYear}`,
            `Property: ${filteredData[0]?.property_name || "All"}`,
            `Status Filter: ${statusFilter || "All"}`,
            `Exported By: ${localStorage.getItem("loginName")}`,
            `Exported On: ${new Date().toLocaleString()}`
        ];

        metadata.forEach(text => {
            doc.text(text, 20, startY);
            startY += 12;
        });

        startY += 10;

        /* TABLE HEADER */

        const head = [exportColumns.map(col => col.label)];

        /* TABLE BODY */

        const body = filteredData.map(row =>
            exportColumns.map(col => {

                const value = row[col.key];

                if (col.isMoney && value != null) {
                    return Number(value).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                    });
                }

                return value ?? "";

            })
        );

        doc.autoTable({
            startY,
            head,
            body,
            styles: { fontSize: 8 },
            headStyles: {
                fillColor: [0, 71, 178],
                textColor: 255
            },
            margin: { left: 20, right: 20 },
            pageBreak: "auto"
        });

        /* PAGE NUMBERS */

        const pageCount = doc.internal.getNumberOfPages();

        for (let i = 1; i <= pageCount; i++) {

            doc.setPage(i);

            doc.setFontSize(8);

            doc.text(
                `Page ${i} of ${pageCount}`,
                pageWidth / 2,
                pageHeight - 20,
                { align: "center" }
            );

        }

        doc.save(
            `Tenant_Audit_Summary_${monthLabel}_${selectedYear}.pdf`
        );

    };

    useEffect(() => {

        axios
            .post(
                `${config.hostname}/usercolumnpreferences/getusercolumns`,
                {
                    login_id: login_id,
                    screen: "tenant_audit_summary"
                },
                configheader
            )
            .then((res) => {

                if (res.data?.columns?.length) {
                    setSelectedColumns(res.data.columns);
                }

                setColumnsLoaded(true);

            })
            .catch((err) => {
                console.error(err);
                setColumnsLoaded(true);
            });

    }, []);

    useEffect(() => {

        if (!columnsLoaded) return;

        const timer = setTimeout(() => {

            axios
                .post(
                    `${config.hostname}/usercolumnpreferences/saveusercolumns`,
                    {
                        login_id: login_id,
                        screen: "tenant_audit_summary",
                        columns: selectedColumns
                    },
                    configheader
                )
                .catch((err) => {
                    console.error(err);
                });

        }, 500);

        return () => clearTimeout(timer);

    }, [selectedColumns, columnsLoaded]);

    const daysInMonth = new Date(selectedYear, monthLabel, 0).getDate();

    const conditionalRowStyles = [
        {
            when: row => (row.live_dates ?? 0) < daysInMonth,
            style: { backgroundColor: 'rgba(211, 47, 47, 0.08)', borderLeft: '3px solid #d32f2f' }
        }
    ];

    const customStyles = {

        headCells: {
            style: {
                fontSize: "13px",
                fontWeight: "600"
            }
        },

        cells: {
            style: {
                fontSize: "13px"
            }
        }

    };

    const stickyStyles = `
        .audit-table-wrapper{ overflow-x:auto; }
        .money{ text-align:right; white-space:nowrap; }
        .diff-pos{ color:#d32f2f; font-weight:600; }
        .diff-zero{  color:#2e7d32; font-weight:600;}
        `;

    return (
        <Fragment>

            <Row className="justify-content-end">

                <Col xs="auto">
                    <Input
                        type="text"
                        placeholder="Search Tenant..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </Col>

                <Col xs="auto">
                    <Input
                        type="select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="MATCHED">Matched</option>
                        <option value="NOT_MATCHED">Not Matched</option>
                        <option value="RECONCILED">Reconciled</option>
                        <option value="PENDING">Pending</option>
                    </Input>
                </Col>

                {/* COLUMN SELECTOR */}

                <Col xs="auto">
                    <div ref={columnDropdownRef} style={{ position: "relative" }}>

                        <Btn attrBtn={{ color: "primary", onClick: () => setColumnDropdownOpen(v => !v) }}>
                            Columns ▼
                        </Btn>

                        {columnDropdownOpen && (

                            <div
                                style={{
                                    position: "absolute",
                                    right: 0,
                                    width: 260,
                                    background: "#fff",
                                    border: "1px solid #ddd",
                                    borderRadius: 8,
                                    boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
                                    zIndex: 9999
                                }}
                            >

                                <label style={{ padding: 10, display: "block" }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedColumns.length === ALL_COLUMNS.length}
                                        onChange={(e) =>
                                            setSelectedColumns(
                                                e.target.checked
                                                    ? ALL_COLUMNS.map((c) => c.key)
                                                    : []
                                            )
                                        }
                                    />
                                    Select All
                                </label>

                                {ALL_COLUMNS.map((col) => (

                                    <label key={col.key} style={{ display: "block", padding: "6px 12px" }}>
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(col.key)}
                                            onChange={() =>
                                                setSelectedColumns((prev) =>
                                                    prev.includes(col.key)
                                                        ? prev.filter((k) => k !== col.key)
                                                        : [...prev, col.key]
                                                )
                                            }
                                        />
                                        {col.label}
                                    </label>

                                ))}

                            </div>

                        )}

                    </div>
                </Col>

                <Col xs="auto" style={{ position: "relative" }}>
                    <Btn
                        attrBtn={{
                            color: "primary",
                            type: "button",
                            onClick: () => handleExport("excel"),
                            onMouseEnter: () => handleMouseEnter("Export to Excel"),
                            onMouseLeave: handleMouseLeave
                        }}
                    >
                        <FontAwesomeIcon icon={faFileExcel} size="lg" />
                    </Btn>

                    {tooltip === "Export to Excel" && (
                        <div
                            style={{
                                position: "absolute",
                                top: "-30px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "black",
                                color: "white",
                                padding: "5px 10px",
                                borderRadius: "3px",
                                whiteSpace: "nowrap",
                                fontSize: "12px",
                                zIndex: 1
                            }}
                        >
                            {tooltip}
                        </div>
                    )}
                </Col>

                <Col xs="auto" style={{ position: "relative" }}>
                    <Btn
                        attrBtn={{
                            color: "primary",
                            type: "button",
                            onClick: () => handleExport("pdf"),
                            onMouseEnter: () => handleMouseEnter("Export to PDF"),
                            onMouseLeave: handleMouseLeave
                        }}
                    >
                        <FontAwesomeIcon icon={faFilePdf} size="lg" />
                    </Btn>

                    {tooltip === "Export to PDF" && (
                        <div
                            style={{
                                position: "absolute",
                                top: "-30px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "black",
                                color: "white",
                                padding: "5px 10px",
                                borderRadius: "3px",
                                whiteSpace: "nowrap",
                                fontSize: "12px",
                                zIndex: 1
                            }}
                        >
                            {tooltip}
                        </div>
                    )}
                </Col>
            </Row>

            <Row className="mt-4">

                {!propertySelected ? (

                    <div className="text-center text-muted p-5">
                        Select a property to view tenants
                    </div>

                ) : (

                    <div className="audit-table-wrapper">

                        <style>{stickyStyles}</style>

                        <DataTable
                            data={filteredData}
                            columns={columns}
                            striped
                            highlightOnHover
                            pagination
                            progressPending={loading}
                            paginationPerPage={20}
                            paginationRowsPerPageOptions={[20, 50, 100]}
                            customStyles={customStyles}
                            conditionalRowStyles={conditionalRowStyles}
                        />

                        <CommonModal
                            isOpen={commentModal}
                            toggler={() => setCommentModal(false)}
                            title={`Audit Comment – ${selectedTenant?.tenant_name || ""} (${selectedTenant?.tenant_code || ""})`}
                            onSave={saveComment}
                        >
                            <Input
                                type="textarea"
                                rows="4"
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                placeholder="Enter comment..."
                            />
                        </CommonModal>

                    </div>

                )}

            </Row>

        </Fragment>
    );
};

export default TenantAuditSummary;